import { useRef, useState } from 'react';
import Sheet from 'react-modal-sheet';
import { scrollY, floatSurfaceSx } from '../../ui/styles';
import { Box, Stack, useMediaQuery } from '../../ui/core';

// Matches hitch-web's booking flow: the map owns the page and the detail
// column floats over it, rather than a map squeezed into a card.
const PANEL_WIDTH = 420;
const PANEL_INSET = 24;

// MOBILE_BAR_H in DashoardLayout. Not imported: that pulls the whole layout
// into every spec that renders this.
const MOBILE_BAR_H = 56;

// On a phone the panel is a bottom sheet (react-modal-sheet, as hitch-web
// uses) with three detents, like Apple Maps: nearly full height, part way, and
// a peek. Snap points are px of the window (fractions ≤ 1, negatives from the
// top), in descending order. The map frames its contents above the middle one.
const SHEET_PEEK = 0.45;
const SNAP_POINTS = [-(MOBILE_BAR_H + 12), SHEET_PEEK, 120];
const INITIAL_SNAP = 1;

// A phone's map is too narrow for map-type, Street View and fullscreen buttons;
// pinch and drag are all it needs. Spread in when the map gets a `sheetPeek`.
export const PHONE_MAP_OPTIONS = { mapTypeControl: false, streetViewControl: false, fullscreenControl: false };

/**
 * Padding for `map.fitBounds` that keeps what's fitted out from under the
 * panel: generous on the left beside the desktop panel, or the strip above the
 * sheet on a phone (`sheetPeek` is what the map's `map` render prop receives).
 */
export const fitPadding = (map, sheetPeek) =>
  sheetPeek
    ? { top: 48, right: 32, left: 32, bottom: Math.round(map.getDiv().clientHeight * sheetPeek) + 24 }
    : { top: 64, right: 64, bottom: 64, left: PANEL_WIDTH + PANEL_INSET * 2 };

const PhoneSheet = ({ children }) => {
  const ref = useRef(null);
  // Which detent the sheet is resting on (0 = top). See Sheet.Content below.
  const [snap, setSnap] = useState(INITIAL_SNAP);
  return (
    <Sheet
      ref={ref}
      isOpen
      // There is no closed state — the sheet is the page's panel. A downward
      // flick asks to close; settle on the peek instead.
      onClose={() => ref.current?.snapTo(SNAP_POINTS.length - 1)}
      snapPoints={SNAP_POINTS}
      initialSnap={INITIAL_SNAP}
      onSnap={setSnap}
      // Its scroll lock blocks touch moves outside the sheet, which is the map.
      disableScrollLocking
      // Above the map (10), below MUI's app bar and drawer (1100+).
      style={{ zIndex: 20 }}
    >
      {/* The library paints a white card; ours follows the theme (dark mode). */}
      <Sheet.Container style={{ background: 'transparent', boxShadow: 'none' }}>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'background.paper',
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            boxShadow: '0 -4px 24px rgba(0,0,0,0.18)',
          }}
        >
          <Sheet.Header>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
              <Box sx={{ width: 36, height: 4, borderRadius: 2, backgroundColor: 'divider' }} />
            </Box>
          </Sheet.Header>
          {/* Apple Maps' rule: below the top detent a swipe on the content moves
              the sheet; at the top it scrolls the content, and the handle pulls
              the sheet back down. Both at once can't work — the sheet's drag
              claims vertical touches (touch-action: pan-x), so the list could
              never scroll, and below the top detent the sheet's lower part is
              off-screen anyway (it is sized to the top detent and slid down). */}
          <Sheet.Content disableDrag={snap === 0}>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }}>
              <Stack spacing={2} sx={{ px: 2, pb: 'calc(16px + env(safe-area-inset-bottom))' }}>
                {children}
              </Stack>
            </Box>
          </Sheet.Content>
        </Box>
      </Sheet.Container>
    </Sheet>
  );
};

/**
 * A full-bleed map with a detail panel floating over its left side — or, on a
 * phone, the whole screen with a bottom sheet over it.
 *
 * `map` is called with the sheet's peek fraction (undefined on desktop) so the
 * map can fit its contents into the part the panel isn't covering.
 */
const MapFirstLayout = ({ map, children }) => {
  const compact = useMediaQuery((theme) => theme.breakpoints.down('md'));

  if (compact) {
    return (
      // On a phone the map IS the page, as in hitch-web's booking flow: pinned
      // to the screen under the top bar, clear of the page's padding and card,
      // so nothing scrolls and the map pans with one finger.
      <Box sx={{ position: 'fixed', top: MOBILE_BAR_H, left: 0, right: 0, bottom: 0, zIndex: 10 }}>
        {map(SHEET_PEEK)}
        <PhoneSheet>{children}</PhoneSheet>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
      {map(undefined)}
      <Stack
        spacing={2}
        sx={{
          position: 'absolute',
          top: PANEL_INSET,
          left: PANEL_INSET,
          bottom: PANEL_INSET,
          width: PANEL_WIDTH,
          maxWidth: `calc(100% - ${PANEL_INSET * 2}px)`,
          p: 2,
          backgroundColor: (theme) => theme.palette.background.paper,
          ...floatSurfaceSx,
          ...scrollY,
        }}
      >
        {children}
      </Stack>
    </Box>
  );
};

export default MapFirstLayout;
